import { Button, SoftButton, SoftButtonLink } from './Button';
import { DATA_SOURCES, FOOD_ROOTS, isCovered } from '@/config/constants';
import { FiBarChart2, FiSettings } from 'react-icons/fi';
import {
    SectionCard,
    SectionCardH1,
    SectionCardHeading,
    SectionCardIcon,
    SectionCardSubH1,
} from './SectionCard';
import {
    formatTimeSeries,
    getPathFromNode,
    hierarchyHintObjToStr,
    parseTikTokTimeSeries,
    parseWeeklySearchTimeSeries,
} from 'utils/helpers';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button as DSButton } from '@/ui/design-system/components/ui/button';
import { Dialog } from '@headlessui/react';
import { FilterCheckbox } from './FilterAccordion';
import { IoMdBusiness } from 'react-icons/io';
import Modal from 'components/Modal';
import SparkTiles from './SparkTiles';
import Stack from './Stack';
import addPredictionToWeeklyTimeSeries from 'utils/addPredictionToWeeklyTimeSeries';
import { endpointWrapper } from 'utils/api/meta.api';
import { getParentIdByAncestorHint } from 'utils/hooks/useGetHierarchy';
import { mixpanelTrack } from 'utils/app';
import { shapeTimeSeries } from 'utils/hooks/sourceDataShaping';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useUserIndustries } from 'utils/hooks';

const SoftButtonBlackText = styled(SoftButton)`
    color: ${({ theme }) => theme.gray_1};
`;
const SoftButtonLinkBlackText = styled(SoftButtonLink)`
    color: ${({ theme }) => theme.gray_1};
`;

const getUserCustomization = () =>
    endpointWrapper({
        domain: 'user/customization',
        method: 'GET',
    }).then((customization) => customization?.homepage);

const useCustomization = () => useQuery('customization', getUserCustomization);

const useMutateCustomization = () => {
    const qc = useQueryClient();
    return useMutation(
        (homepage) =>
            endpointWrapper({
                domain: 'user/customization',
                method: 'POST',
                data: { customization: { homepage } },
            }),
        {
            onMutate: (homepage) => {
                qc.cancelQueries('customization');
                const snapshot = qc.getQueryData('customization');
                qc.setQueryData('customization', homepage);
                return () => qc.setQueryData('customization', snapshot);
            },
        },
    );
};

const CustomizeIcon = styled(FiSettings)`
    color: ${({ theme }) => theme.purple_1};
`;

const getTrends = ({ queryKey }) =>
    endpointWrapper({ domain: 'trends', data: queryKey[1] }).then((results) =>
        results
            .slice(0, 4)
            .map(
                ({
                    data,
                    name,
                    weekly_timeseries_google,
                    weekly_timeseries_tiktok,
                    monthly_timeseries_google,
                    ...result
                }) => {
                    const textarea = document.createElement('textarea');
                    textarea.innerHTML = name;
                    const maybeTikTokTimeSeries = weekly_timeseries_tiktok
                        ? parseTikTokTimeSeries(weekly_timeseries_tiktok)
                        : null;
                    const maybeWeeklyGoogleTimeSeries = weekly_timeseries_google
                        ? parseWeeklySearchTimeSeries(weekly_timeseries_google)
                        : null;
                    const maybeMonthlyGoogleTimeSeries =
                        monthly_timeseries_google
                            ? formatTimeSeries(monthly_timeseries_google)
                            : null;
                    const weeklyGoogleTimeSeriesWithPrediction =
                        addPredictionToWeeklyTimeSeries({
                            tikTokTimeSeries: maybeTikTokTimeSeries,
                            monthlyTimeSeries: maybeMonthlyGoogleTimeSeries,
                            weeklyTimeSeries: maybeWeeklyGoogleTimeSeries,
                        });
                    return {
                        ...result,
                        ...(result.type === 'category' && {
                            depth: 'cat5',
                        }),
                        name: textarea.value,
                        monthlyGoogleTimeSeries: maybeMonthlyGoogleTimeSeries,
                        weeklyGoogleTimeSeries:
                            weeklyGoogleTimeSeriesWithPrediction,
                        weeklyTikTokTimeSeries: maybeTikTokTimeSeries,
                    };
                },
            ),
    );

const getTrendsGoogle = ({ queryKey }) =>
    endpointWrapper({ domain: 'trends/google', data: queryKey[1] }).then(
        (results) =>
            results.slice(0, 4).map(({ data, name, ...result }) => {
                const textarea = document.createElement('textarea');
                textarea.innerHTML = name;
                return {
                    ...result,
                    ...(result.type === 'category' && {
                        depth: 'cat5',
                    }),
                    name: textarea.value,
                    data: formatTimeSeries(data),
                };
            }),
    );

const getTrendsPopularity = ({ queryKey }) =>
    endpointWrapper({
        domain: 'trends/popularity',
        data: {
            ...queryKey[1],
            lifeCycle: queryKey[1].lifeCycle
                ? [queryKey[1].lifeCycle]
                : ['growth'],
        },
    }).then((results) =>
        results.slice(0, 4).map((result) => {
            const timeSeriesGoogle = result.google_time_series
                ? shapeTimeSeries(result.google_time_series?.time_series)
                : null;
            const timeSeriesTikTok = result.tiktok_time_series
                ? shapeTimeSeries(result.tiktok_time_series?.time_series)
                : null;
            const timeSeriesInstagram = result.instagram_time_series
                ? shapeTimeSeries(result.instagram_time_series?.time_series)
                : null;
            return {
                ...(result.type === 'category' && {
                    depth: 'cat5',
                }),
                id: result.id,
                name: result.name,
                type: result.type,
                YoY: result.YoY,
                increase_YoY: result.increase_YoY,
                timeSeriesGoogle,
                timeSeriesTikTok,
                timeSeriesInstagram,
            };
        }),
    );

const SectionCardNoPadding = styled(SectionCard)`
    padding: 0;
`;
const SectionCardHeadingPadded = styled(SectionCardHeading)`
    padding: 14px 18px 0px;
`;

const options = [
    {
        name: 'beauty',
        id: '6d408533-6979-43aa-80af-4d88bf0be860',
        hierarchy_hint: { cat1: 'beauty' },
    },
    {
        name: 'hair',
        id: 'a1d01543-3b04-426e-a194-29d9bd8f9b0c',
        hierarchy_hint: { cat1: 'beauty', cat2: 'hair' },
    },
    {
        name: 'skincare',
        id: '16235dff-c4cc-401c-a774-f9304c5ad061',
        hierarchy_hint: { cat1: 'beauty', cat2: 'skincare' },
    },
    {
        name: 'makeup',
        id: 'e74ea13b-c24d-4ece-811d-eb49452eafb5',
        hierarchy_hint: { cat1: 'beauty', cat2: 'makeup' },
    },
    {
        name: 'grocery',
        id: '0c542a5d-47f2-4a87-aee6-5d28dadcaa83',
        hierarchy_hint: { cat1: 'grocery' },
    },
    {
        name: 'beverages',
        id: 'f4e08c51-6012-4743-8371-a99940aca7f7',
        hierarchy_hint: { cat1: 'beverages' },
    },
    {
        name: 'culinary',
        id: '00614b3b-7714-4e4b-9934-70a939c9d720',
        hierarchy_hint: { cat1: 'culinary' },
    },
    {
        name: 'fragrance',
        id: 'cb1700c7-f195-484f-acd6-3d0d803e0f84',
        hierarchy_hint: { cat1: 'beauty', cat2: 'fragrance' },
    },
];

const CategoryFieldset = styled.fieldset`
    all: unset;
    display: grid;
    gap: ${({ theme }) => theme.space_5}px;

    @media screen and (min-width: ${({ theme }) => theme.bp.sm}px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const Capitalize = styled.span`
    text-transform: capitalize;
`;

const LifeCycleFieldset = styled.fieldset`
    all: unset;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${({ theme }) => theme.space_5}px;
`;

function ModalBody({
    preferences,
    onSubmit,
    close,
    roots,
    options,
    enableLifeCycleSelect,
}) {
    const categoryId =
        preferences && preferences.categoryId ? preferences.categoryId : null;
    const initialLifeCycle = preferences?.lifeCycle || 'growth';

    const userOptions = roots
        ? options.filter(({ name }) =>
              FOOD_ROOTS.includes(name)
                  ? roots.find(({ name: rootName }) => rootName === name)
                  : roots.find(({ name: rootName }) => rootName === 'beauty'),
          )
        : null;

    const [nextCategoryId, setNextCategoryId] = useState(categoryId);
    const [nextLifeCycle, setNextLifeCycle] = useState(initialLifeCycle);

    useEffect(() => {
        if (nextCategoryId === null && categoryId) {
            setNextCategoryId(categoryId);
        }
    }, [nextCategoryId, setNextCategoryId, categoryId]);

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit({
                    preferredCategory: nextCategoryId,
                    lifeCycle: nextLifeCycle,
                });
            }}
        >
            <Stack as="header" $space="8px">
                <Dialog.Title>Customize category trends</Dialog.Title>
                <Dialog.Description>
                    Select categories you would like to see on your homepage.
                </Dialog.Description>
            </Stack>
            <main>
                <CategoryFieldset>
                    {userOptions
                        ? userOptions.map(({ name, id }) => {
                              return (
                                  <FilterCheckbox
                                      checked={nextCategoryId === id}
                                      onChange={() => setNextCategoryId(id)}
                                      key={id}
                                  >
                                      {name}
                                  </FilterCheckbox>
                              );
                          })
                        : null}
                </CategoryFieldset>
                {enableLifeCycleSelect && (
                    <>
                        <h3 css="margin-top: 16px; margin-bottom: 8px; font-size: 14px; font-weight: 500; text-transform: capitalize;">
                            Life Cycle
                        </h3>
                        <LifeCycleFieldset>
                            {Object.entries(lifeCycleLabels).map(
                                ([value, label]) => (
                                    <FilterCheckbox
                                        checked={nextLifeCycle === value}
                                        onChange={() => setNextLifeCycle(value)}
                                        key={value}
                                    >
                                        {label}
                                    </FilterCheckbox>
                                ),
                            )}
                        </LifeCycleFieldset>
                    </>
                )}
            </main>
            <Stack as="footer" $direction="row" $space="6px">
                <Button intent="secondary" onClick={() => close()}>
                    Cancel
                </Button>
                <Button type="submit">Save</Button>
            </Stack>
        </form>
    );
}

const lifeCycleLabels = {
    early: 'Early',
    growth: 'Growth',
    mainstream: 'Mainstream',
};

function TrendsWidgetSubHeading({
    trendLabel,
    categoryName,
    lifeCycle,
    onOpenModal,
}) {
    const stageLabel = lifeCycle
        ? lifeCycleLabels[lifeCycle] || 'Growth'
        : 'Early';
    return (
        <SectionCardSubH1>
            <span>
                Discover{' '}
                {onOpenModal ? (
                    <DSButton
                        variant="link"
                        className="p-0 h-auto text-base font-normal text-inherit underline"
                        onClick={onOpenModal}
                        type="button"
                    >
                        {stageLabel} Stage
                    </DSButton>
                ) : (
                    <>{stageLabel} Stage</>
                )}{' '}
                {trendLabel} in{' '}
                {onOpenModal ? (
                    <DSButton
                        variant="link"
                        className="p-0 h-auto text-base font-normal text-inherit underline capitalize"
                        onClick={onOpenModal}
                        type="button"
                    >
                        {categoryName}
                    </DSButton>
                ) : (
                    <Capitalize>{categoryName}</Capitalize>
                )}
                .
            </span>
        </SectionCardSubH1>
    );
}

function CategoryTrendsWidget({
    trendType,
    customizationKey,
    options,
    queryKey,
    queryFn,
    getAllTrendsPath,
    allTrendsLabel,
    IconComponent,
    sectionTitle,
    trendLabel,
    TileComponent,
    mapTrendsQueryDataFn,
    dataSource,
    enableLifeCycleSelect,
}) {
    const industriesQuery = useUserIndustries();
    const roots = industriesQuery.data?.filter(
        ({ name }) => !['audience', 'ingredients'].includes(name),
    );
    const firstRoot =
        industriesQuery.data && industriesQuery.data[0]
            ? industriesQuery.data[0]
            : null;
    const maybeCountry = useSelector(({ geo }) => geo);
    const countryId = maybeCountry ? maybeCountry.id : null;
    const customizationQuery = useCustomization();
    const { mutate: setHomepage } = useMutateCustomization();
    const widgetConfig = customizationQuery.data?.[customizationKey];
    const categoryId = widgetConfig?.category
        ? widgetConfig.category
        : ['error', 'success'].includes(customizationQuery.status) && firstRoot
          ? firstRoot.id
          : null;
    const lifeCycle = enableLifeCycleSelect
        ? widgetConfig?.lifeCycle || 'growth'
        : undefined;
    const category = options.find(({ id }) => id === categoryId);
    const categoryName = category ? category.name : null;
    const maybeRedactedHierarchyHintStr =
        category && category.hierarchy_hint
            ? hierarchyHintObjToStr(category.hierarchy_hint)
            : null;
    const [isOpen, setIsOpen] = useState(false);

    // Check if category is covered by any data source
    // A category is "redacted" (should not show data) if it's not covered by any data source
    const cat1 = category?.hierarchy_hint?.cat1;
    const isRedactedCategory =
        category &&
        maybeCountry &&
        cat1 &&
        !isCovered({
            geo: maybeCountry.text_id,
            dataSource,
            cat1,
        });

    const trendsQuery = useQuery(
        [
            queryKey,
            {
                countryId,
                categoryId,
                trendType,
                ...(lifeCycle && { lifeCycle }),
            },
        ],
        isRedactedCategory ? () => Promise.resolve([]) : queryFn,
        { enabled: !!(countryId && categoryId && trendType) },
    );

    const mixpanelMeta = {
        contentKey: 'CategoryTrendsWidget',
        country: maybeCountry ? maybeCountry.name : null,
        category: categoryName,
        trendType,
    };

    return (
        <>
            <SectionCardNoPadding>
                <SectionCardHeadingPadded>
                    <SectionCardIcon Icon={IconComponent} />
                    <SectionCardH1>
                        <span>{sectionTitle}.</span>
                    </SectionCardH1>
                    <TrendsWidgetSubHeading
                        trendLabel={trendLabel}
                        categoryName={categoryName}
                        lifeCycle={lifeCycle}
                        onOpenModal={
                            enableLifeCycleSelect
                                ? () => setIsOpen(true)
                                : undefined
                        }
                    />
                    <div css="display: inline-flex; align-items: center; margin-left: auto;">
                        <SoftButtonBlackText
                            css="min-width: unset;"
                            size="sm"
                            IconLeft={CustomizeIcon}
                            onClick={() => setIsOpen(true)}
                            mixpanelMeta={mixpanelMeta}
                            mixpanelContext="CustomizeButton"
                        >
                            Customize
                        </SoftButtonBlackText>
                        <span
                            css={`
                                border-left: solid 1px
                                    ${({ theme }) => theme.gray_5};
                                min-height: 16px;
                                max-height: 16px;
                                margin: 0px ${({ theme }) => theme.space_3}px;
                            `}
                        />
                        <SoftButtonLinkBlackText
                            to={getAllTrendsPath({
                                trendType,
                                categoryId,
                                lifeCycle,
                            })}
                            mixpanelMeta={mixpanelMeta}
                            mixpanelContext="AllTrendsButton"
                        >
                            {allTrendsLabel}
                        </SoftButtonLinkBlackText>
                    </div>
                </SectionCardHeadingPadded>
                <SparkTiles.Tiles
                    {...(isRedactedCategory && {
                        emptyMessage: `No ${trendLabel} to display. Coverage may not be available for this region.`,
                    })}
                    status={trendsQuery.status}
                    tiles={
                        trendsQuery.data
                            ? trendsQuery.data.map((...args) =>
                                  mapTrendsQueryDataFn(
                                      {
                                          hierarchy_hint:
                                              category?.hierarchy_hint,
                                          mixpanelMeta,
                                      },
                                      ...args,
                                  ),
                              )
                            : null
                    }
                    Tile={TileComponent}
                    Grid={SparkTiles.GridScroll}
                />
            </SectionCardNoPadding>
            <Modal open={isOpen} onClose={() => setIsOpen(false)}>
                {isOpen ? (
                    <ModalBody
                        preferences={{ categoryId, lifeCycle }}
                        onSubmit={({
                            preferredCategory,
                            lifeCycle: nextLifeCycle,
                        }) => {
                            if (preferredCategory) {
                                const updates = {
                                    ...(customizationQuery.data || {}),
                                    [customizationKey]: {
                                        ...widgetConfig,
                                        category: preferredCategory,
                                        ...(nextLifeCycle && {
                                            lifeCycle: nextLifeCycle,
                                        }),
                                    },
                                };
                                setHomepage(updates);
                                mixpanelTrack('Customize', {
                                    widget: 'CategoryTrends',
                                    category: options.find(
                                        ({ id }) => id === preferredCategory,
                                    ).name,
                                    country: maybeCountry
                                        ? maybeCountry.name
                                        : null,
                                    trendType,
                                    ...(nextLifeCycle && {
                                        lifeCycle: nextLifeCycle,
                                    }),
                                });
                            }
                            setIsOpen(false);
                        }}
                        close={() => setIsOpen(false)}
                        roots={roots}
                        options={options}
                        enableLifeCycleSelect={enableLifeCycleSelect}
                    />
                ) : null}
            </Modal>
        </>
    );
}

const getAllTrendsPathOverview = ({ trendType, categoryId }) => {
    return `/explore/${trendType === 'cat_fives' ? 'trends' : 'brands'}/${categoryId}?tikTokCluster=tiktok+sustained+riser%2Ctiktok+rising+star&healthStats=average+monthly+searches-0%2Caverage+monthly+searches-2%2Caverage+monthly+searches-4&sort=tiktok-views+YoY+high+to+low`;
};

const getAllTrendsPathOverviewV2 = ({ trendType, categoryId, lifeCycle }) => {
    const lifeCycleParam = lifeCycle
        ? `&life_cycle=${encodeURIComponent(lifeCycle)}`
        : '';
    return `/explore/${trendType === 'cat_fives' ? 'trends' : 'brands'}/${categoryId}?sort=Popularity+YoY+increase+high+to+low${lifeCycleParam}`;
};

const mapTrendsQueryDataFnOverview = (
    { hierarchy_hint, mixpanelMeta },
    {
        id,
        name,
        google_YoY,
        tiktok_YoY,
        monthlyGoogleTimeSeries,
        weeklyGoogleTimeSeries,
        weeklyTikTokTimeSeries,
        type,
        depth,
        hierarchies,
    },
) => ({
    key: id,
    title: name,
    YoY: google_YoY,
    non_cum_YoY_rolling: tiktok_YoY,
    monthlyGoogleTimeSeries,
    weeklyGoogleTimeSeries,
    weeklyTikTokTimeSeries,
    to: getPathFromNode(
        { id, depth, type },
        getParentIdByAncestorHint(hierarchies, hierarchy_hint),
    ),
    mixpanelMeta,
});

const getAllTrendsPathGoogle = ({ trendType, categoryId }) => {
    return `/explore/google/${trendType === 'cat_fives' ? 'trends' : 'brands'}/${categoryId}?&sort=YoY+high+to+low&cluster=sustained+riser%2Cseasonal+riser%2Crising+star&healthStats=average+monthly+searches-0%2Caverage+monthly+searches-2%2Caverage+monthly+searches-4`;
};

const mapTrendsQueryDataFnGoogle = (
    { hierarchy_hint, mixpanelMeta },
    { id, data, name, YoY, type, depth, hierarchies },
) => ({
    key: id,
    title: name,
    data,
    YoY,
    to: getPathFromNode(
        { id, depth, type },
        getParentIdByAncestorHint(hierarchies, hierarchy_hint),
    ),
    mixpanelMeta,
});

export const CategoryTrendsGoogleWidget = () => (
    <CategoryTrendsWidget
        trendType="cat_fives"
        customizationKey="categoryTrendsWidget"
        options={options}
        queryKey="trends-google"
        queryFn={getTrendsGoogle}
        getAllTrendsPath={getAllTrendsPathGoogle}
        allTrendsLabel="All Trends"
        IconComponent={FiBarChart2}
        sectionTitle="Category Trends"
        trendLabel="trends"
        TileComponent={SparkTiles.Tile}
        mapTrendsQueryDataFn={mapTrendsQueryDataFnGoogle}
        dataSource={DATA_SOURCES.GOOGLE}
    />
);

export const CategoryTrendsBrandsGoogleWidget = () => (
    <CategoryTrendsWidget
        trendType="brands"
        customizationKey="brandTrendsWidget"
        options={options}
        queryKey="trends-google"
        queryFn={getTrendsGoogle}
        getAllTrendsPath={getAllTrendsPathGoogle}
        allTrendsLabel="All Brands"
        IconComponent={IoMdBusiness}
        sectionTitle="Brands"
        trendLabel="brands"
        TileComponent={SparkTiles.Tile}
        mapTrendsQueryDataFn={mapTrendsQueryDataFnGoogle}
        dataSource={DATA_SOURCES.GOOGLE}
    />
);

const mapTrendsQueryDataFnPopularity = (
    { hierarchy_hint, mixpanelMeta },
    {
        id,
        name,
        YoY,
        timeSeriesGoogle,
        timeSeriesTikTok,
        timeSeriesInstagram,
        type,
        depth,
        hierarchies,
    },
    idx,
) => ({
    key: `${id}-${idx}`,
    title: name,
    YoY,
    timeSeriesGoogle,
    timeSeriesTikTok,
    timeSeriesInstagram,
    to: getPathFromNode(
        { id, depth, type },
        getParentIdByAncestorHint(hierarchies, hierarchy_hint),
    ),
    mixpanelMeta,
});

export const CategoryTrendsPopularityWidget = () => (
    <CategoryTrendsWidget
        trendType="cat_fives"
        customizationKey="categoryTrendsWidgetOverview"
        options={options}
        queryKey="trends-overview"
        queryFn={getTrendsPopularity}
        getAllTrendsPath={getAllTrendsPathOverviewV2}
        allTrendsLabel="All Trends"
        IconComponent={FiBarChart2}
        sectionTitle="Category Trends"
        trendLabel="trends"
        TileComponent={SparkTiles.OverviewTileV2}
        mapTrendsQueryDataFn={mapTrendsQueryDataFnPopularity}
        dataSource={DATA_SOURCES.POPULARITY}
        enableLifeCycleSelect
    />
);

export const CategoryTrendsBrandsPopularityWidget = () => (
    <CategoryTrendsWidget
        trendType="brands"
        customizationKey="brandTrendsWidgetOverview"
        options={options}
        queryKey="trends-overview"
        queryFn={getTrendsPopularity}
        getAllTrendsPath={getAllTrendsPathOverviewV2}
        allTrendsLabel="All Brands"
        IconComponent={IoMdBusiness}
        sectionTitle="Brands"
        trendLabel="brands"
        TileComponent={SparkTiles.OverviewTileV2}
        mapTrendsQueryDataFn={mapTrendsQueryDataFnPopularity}
        dataSource={DATA_SOURCES.POPULARITY}
        enableLifeCycleSelect
    />
);
